import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
    applyColgroupWidths,
    convertFileToBase64,
    Indent,
    LineHeight,
    OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan,
    USER_INPUT_DEBOUNCE_TIME,
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import { IEquityMovement, IEquityMovementEntry, IEquityMovementSchedule } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { IEquityMovementScheduleDates } from '../../interfaces/equity-movement-schedule-dates.interface';
import { IEquityMovementScheduleFli } from '../../interfaces/equity-movement-schedule-fli.interface';
import { IEmsPeriodChange, IEmsPeriodColumn } from '../../interfaces/equity-movement-schedule-period-column.interface';

/** Print ceiling: the Word template fits 8 columns plus the row-name column (9 in total). */
const MAX_EMS_COLUMNS = 8;

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-equity-movement-schedule-page',
    templateUrl: 'equity-movement-schedule.page.html',
    styleUrls: ['equity-movement-schedule.page.scss'],
})
export class EquityMovementSchedulePage implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() flis: IEquityMovementScheduleFli[] = [];

    @Input() dates: IEquityMovementScheduleDates | undefined;

    @Input() hasNoPreviousData = false;

    @Input() reportLocale: string | undefined;

    @Input() equityMovementSchedule: IEquityMovementSchedule | null = null;

    @Input() structureDiff: { newFliNames: string[]; removedFliNames: string[] } = { newFliNames: [], removedFliNames: [] };

    @Output() equityMovementScheduleValue = new EventEmitter<IEquityMovementSchedule>();

    @Output() equityMovementSchedulePrefilledValue = new EventEmitter<IEquityMovementSchedule>();

    public formGroup: UntypedFormGroup;

    private workingEntries: IEquityMovementEntry[] = [];

    previousColumns: IEmsPeriodColumn[] = [];
    currentColumns: IEmsPeriodColumn[] = [];

    previousOpeningTitle = '';
    previousClosingTitle = '';
    currentOpeningTitle = '';
    currentClosingTitle = '';

    showIntro = false;
    showNote = false;

    private structureChangesHandled = false;

    columnPendingDelete: string | null = null;


    private scheduleInputReceived = false;

    public tiptapEditorAbove: Editor | null = null;
    public tiptapEditorBelow: Editor | null = null;
    public tiptapValueAbove = '';
    public tiptapValueBelow = '';

    @ViewChild('tiptapHostAbove') tiptapHostAbove?: ElementRef<HTMLDivElement>;
    @ViewChild('tiptapHostBelow') tiptapHostBelow?: ElementRef<HTMLDivElement>;

    constructor(private readonly i18next: I18NextService) {
        this.initializeForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['structureDiff'] != null) {
            this.structureChangesHandled = false;
        }

        const scheduleChange = changes['equityMovementSchedule'];
        if (scheduleChange != null) {
            this.scheduleInputReceived = true;
        }
        if (scheduleChange != null && scheduleChange.currentValue != null) {
            const schedule = scheduleChange.currentValue as IEquityMovementSchedule;
            this.workingEntries = cloneDeep<IEquityMovementEntry[]>(schedule.equityMovementEntries);

            const aboveCtrl = this.formGroup.get('textAdditionAboveEms');
            const belowCtrl = this.formGroup.get('textAdditionBelowEms');
            if (aboveCtrl != null && belowCtrl != null) {
                aboveCtrl.setValue(schedule.textAdditionAboveEms);
                belowCtrl.setValue(schedule.textAdditionBelowEms);
            }
            this.syncTiptapFromForm();
        }

        if (
            changes['equityMovementSchedule'] != null ||
            changes['flis'] != null ||
            changes['hasNoPreviousData'] != null ||
            changes['dates'] != null
        ) {
            this.setDefaultTitles();
            this.rebuildColumns();
        }

        if (scheduleChange != null && scheduleChange.currentValue != null) {
            this.equityMovementSchedulePrefilledValue.emit(this.assembleSchedule());
        }
    }

    private tContent(key: string): string {
        return this.i18next.t(key, this.reportLocale != null ? { lng: this.reportLocale } : undefined).toString();
    }

    private setDefaultTitles(): void {
        if (this.dates == null) {
            return;
        }
        const schedule = this.equityMovementSchedule;
        // i18next returns the KEY itself when a translation is missing — never show that. Falling back
        // to a hardcoded label would print one language to every reader, so fall back to the bare date.
        const rowTitle = this.tContent('aumico_annual_report_ems_row_title');
        const safeRowTitle = rowTitle !== '' && rowTitle !== 'aumico_annual_report_ems_row_title' ? rowTitle : '';
        const legacyTitles = this.migratedLegacyTitles();
        this.previousOpeningTitle =
            legacyTitles?.previousOpeningTitle ||
            schedule?.previousOpeningTitle ||
            this.withDate(safeRowTitle, this.dates.previousYearOpeningDate);
        this.previousClosingTitle =
            legacyTitles?.previousClosingTitle ||
            schedule?.previousClosingTitle ||
            this.withDate(safeRowTitle, this.dates.previousYearClosingDate);
        this.currentOpeningTitle =
            legacyTitles?.currentOpeningTitle ||
            schedule?.currentOpeningTitle ||
            this.withDate(safeRowTitle, this.dates.currentYearOpeningDate);
        this.currentClosingTitle =
            legacyTitles?.currentClosingTitle ||
            schedule?.currentClosingTitle ||
            this.withDate(safeRowTitle, this.dates.currentYearClosingDate);
    }

    private initializeForm(): void {
        this.formGroup = new UntypedFormGroup({
            textAdditionAboveEms: new UntypedFormControl(null),
            textAdditionBelowEms: new UntypedFormControl(null),
        });
    }

    ngOnInit(): void {
        this.formGroup.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.emitSchedule();
            });
    }

    ngAfterViewInit(): void {
        this.ensureTiptapEditors();
        this.syncTiptapFromForm();
    }

    ngOnDestroy(): void {
        this.tiptapEditorAbove?.destroy();
        this.tiptapEditorBelow?.destroy();
        this.tiptapEditorAbove = null;
        this.tiptapEditorBelow = null;
    }

    private migratedLegacyTitles(): {
        previousOpeningTitle?: string;
        previousClosingTitle?: string;
        currentOpeningTitle?: string;
        currentClosingTitle?: string;
    } | null {
        const schedule = this.equityMovementSchedule;
        const dates = this.dates;
        if (schedule == null || dates == null || schedule.previousClosingTitle !== undefined) {
            return null;
        }

        const previousOpening = this.filledTitle(schedule.previousOpeningTitle);
        const currentOpening = this.filledTitle(schedule.currentOpeningTitle);
        const currentClosing = this.filledTitle(schedule.currentClosingTitle);
        if (previousOpening == null || currentOpening == null || currentClosing == null) {
            return null;
        }

        if (this.hasNoPreviousData) {
            return {
                currentOpeningTitle: this.withDate(previousOpening, dates.previousYearOpeningDate),
                currentClosingTitle: this.withDate(currentClosing, dates.currentYearClosingDate),
            };
        }

        return {
            previousOpeningTitle: this.withDate(previousOpening, dates.previousYearOpeningDate),
            previousClosingTitle: this.withDate(currentOpening, dates.previousYearClosingDate),
            currentOpeningTitle: this.withDate(currentOpening, dates.currentYearOpeningDate),
            currentClosingTitle: this.withDate(currentClosing, dates.currentYearClosingDate),
        };
    }

    private filledTitle(title: string | null | undefined): string | null {
        return typeof title === 'string' && title !== '' ? title : null;
    }

    private withDate(title: string, date: string | undefined): string {
        return [title, date].filter((part) => part != null && part !== '').join(' ');
    }

    private yearOf(date: string | undefined): string {
        return date?.match(/\d{4}/)?.[0] ?? '';
    }

    /**
     * Header of a period box. A regular business year keeps the familiar wording; a quarter or a
     * shortened period is spelled out, because a single year number would misstate it. The year is
     * always taken from the CLOSING date, so an off-calendar business year (e.g. 07/2024–06/2025)
     * is labelled consistently in both period tables.
     */
    periodLabel(isFullYear: boolean | undefined, openingDate: string | undefined, closingDate: string | undefined): string {
        if (isFullYear === true) {
            return `${this.tContent('aumico_annual_report_ems_business_year')} ${this.yearOf(closingDate)}`;
        }
        if (openingDate != null && closingDate != null) {
            return `${openingDate} – ${closingDate}`;
        }
        return closingDate ?? openingDate ?? '';
    }

    private rebuildColumns(): void {
        const hasNothingSaved = this.scheduleInputReceived && this.equityMovementSchedule == null;
        const seeded = hasNothingSaved && this.workingEntries.length === 0 && this.flis.length > 0;
        if (seeded) {
            this.workingEntries = this.seedEntriesFromFlis();
        }
        this.ensureColumnCaptions();
        if (seeded) {
            this.equityMovementSchedulePrefilledValue.emit(this.assembleSchedule());
        }
        this.previousColumns = this.hasNoPreviousData ? [] : this.buildPeriodColumns('previous');
        this.currentColumns = this.buildPeriodColumns('current');
    }

    private seedEntriesFromFlis(): IEquityMovementEntry[] {
        const systemEntries: IEquityMovementEntry[] = this.flis.map((fli) => ({
            fliName: fli.fliName,
            customColType: 'SYSTEM',
            previousMovements: [],
            currentMovements: [],
        }));
        const totalEntry: IEquityMovementEntry = {
            fliName: `TOTAL_${uuidv4()}`,
            customColType: 'TOTAL',
            customColumnName: this.tContent('aumico_annual_report_ems_total_equity_header_text'),
            sumUpTotalColIDs: systemEntries.map((entry) => entry.fliName),
            previousMovements: [],
            currentMovements: [],
        };
        return [...systemEntries, totalEntry];
    }

    private fliById(id: string): IEquityMovementScheduleFli | undefined {
        return this.flis.find((fli) => fli.fliName === id);
    }

    private buildPeriodColumns(period: 'previous' | 'current'): IEmsPeriodColumn[] {
        return this.workingEntries.map((entry): IEmsPeriodColumn => {
            const fli = this.fliById(entry.fliName);
            const type = entry.customColType ?? 'SYSTEM';
            const title = this.displayNameFor(entry.fliName);
            const editableOpening = type === 'USERDEFINED';

            if (period === 'previous') {
                const [opening, ...movements] = entry.previousMovements;
                const mappedOpening = fli?.previousYearOpeningBalance ?? null;
                return {
                    id: entry.fliName,
                    type,
                    title,
                    opening: mappedOpening ?? opening?.value ?? null,
                    closingTarget: type === 'SYSTEM' ? (fli?.currentYearOpeningBalance ?? null) : null,
                    openingEditable: mappedOpening == null || editableOpening,
                    sumUpTotalColIDs: entry.sumUpTotalColIDs,
                    movements,
                };
            }

            return {
                id: entry.fliName,
                type,
                title,
                opening: this.hasNoPreviousData ? (entry.previousMovements[0]?.value ?? null) : this.rollforwardOpening(entry),
                closingTarget: type === 'SYSTEM' ? (fli?.currentYearClosingBalance ?? null) : null,
                openingEditable: this.hasNoPreviousData || editableOpening,
                sumUpTotalColIDs: entry.sumUpTotalColIDs,
                movements: entry.currentMovements,
            };
        });
    }

    private rollforwardOpening(entry: IEquityMovementEntry): number | null {
        const type = entry.customColType ?? 'SYSTEM';
        if (type === 'SYSTEM') {
            return this.fliById(entry.fliName)?.currentYearOpeningBalance ?? null;
        }
        if (type === 'TOTAL') {
            return null;
        }
        return entry.previousMovements.reduce((sum, movement) => sum + (movement.value ?? 0), 0);
    }

    onPreviousPeriodChanged(change: IEmsPeriodChange): void {
        this.previousOpeningTitle = change.openingTitle;
        this.previousClosingTitle = change.closingTitle;
        change.columns.forEach((col) => {
            const entry = this.workingEntries.find((candidate) => candidate.fliName === col.id);
            if (entry != null) {
                entry.previousMovements = [{ name: null, value: col.opening }, ...col.movements];
            }
        });
        this.emitSchedule();
    }

    onCurrentPeriodChanged(change: IEmsPeriodChange): void {
        this.currentOpeningTitle = change.openingTitle;
        this.currentClosingTitle = change.closingTitle;
        change.columns.forEach((col) => {
            const entry = this.workingEntries.find((candidate) => candidate.fliName === col.id);
            if (entry == null) {
                return;
            }
            entry.currentMovements = col.movements;
            if (this.hasNoPreviousData) {
                entry.previousMovements = [{ name: null, value: col.opening }];
            }
        });
        this.emitSchedule();
    }

    onColumnRename(event: { id: string; title: string }): void {
        const entry = this.workingEntries.find((candidate) => candidate.fliName === event.id);
        if (entry != null) {
            entry.customColumnName = event.title;
            this.rebuildColumns();
            this.emitSchedule();
        }
    }

    onColumnDelete(id: string): void {
        this.columnPendingDelete = id;
    }

    onCancelColumnDelete(): void {
        this.columnPendingDelete = null;
    }

    onConfirmColumnDelete(): void {
        const id = this.columnPendingDelete;
        this.columnPendingDelete = null;
        if (id == null) {
            return;
        }
        this.workingEntries = this.workingEntries.filter((entry) => entry.fliName !== id);
        this.workingEntries.forEach((entry) => {
            if (entry.sumUpTotalColIDs != null) {
                entry.sumUpTotalColIDs = entry.sumUpTotalColIDs.filter((sourceId) => sourceId !== id);
            }
        });
        this.rebuildColumns();
        this.emitSchedule();
    }

    onColumnToggleSource(event: { totalId: string; sourceId: string }): void {
        const total = this.workingEntries.find((candidate) => candidate.fliName === event.totalId);
        if (total == null) {
            return;
        }
        const sourceIds = total.sumUpTotalColIDs ?? [];
        total.sumUpTotalColIDs = sourceIds.includes(event.sourceId)
            ? sourceIds.filter((sourceId) => sourceId !== event.sourceId)
            : [...sourceIds, event.sourceId];
        this.rebuildColumns();
        this.emitSchedule();
    }

    onColumnReorder(event: { previousIndex: number; currentIndex: number }): void {
        moveItemInArray(this.workingEntries, event.previousIndex, event.currentIndex);
        this.rebuildColumns();
        this.emitSchedule();
    }

    /** The print template renders at most MAX_EMS_COLUMNS columns (plus the row-name column). */
    get columnLimitReached(): boolean {
        return this.workingEntries.length >= MAX_EMS_COLUMNS;
    }

    get showStructureBanner(): boolean {
        return (
            !this.structureChangesHandled &&
            (this.structureDiff.newFliNames.length > 0 || this.structureDiff.removedFliNames.length > 0)
        );
    }

    get structureBannerMessage(): string {
        const parts = [this.i18next.t('aumico_annual_report_ems_structure_diff_intro').toString()];
        if (this.structureDiff.newFliNames.length > 0) {
            parts.push(
                this.i18next
                    .t('aumico_annual_report_ems_structure_diff_new', {
                        names: this.structureDiff.newFliNames.map((id) => this.displayNameFor(id)).join(', '),
                    })
                    .toString(),
            );
        }
        if (this.structureDiff.removedFliNames.length > 0) {
            parts.push(
                this.i18next
                    .t('aumico_annual_report_ems_structure_diff_removed', {
                        names: this.structureDiff.removedFliNames.map((id) => this.displayNameFor(id)).join(', '),
                    })
                    .toString(),
            );
        }
        return parts.join(' ');
    }

    private resolveCaption(fliName: string): string | null {
        const entry = this.workingEntries.find((candidate) => candidate.fliName === fliName);
        if (entry?.customColumnName) {
            return entry.customColumnName;
        }
        if (entry?.customColType === 'TOTAL') {
            const isFirstTotal =
                this.workingEntries.find((candidate) => candidate.customColType === 'TOTAL')?.fliName === fliName;
            return this.tContent(
                isFirstTotal
                    ? 'aumico_annual_report_ems_total_equity_header_text'
                    : 'aumico_annual_report_ems_new_total_column',
            );
        }
        const caption = this.fliById(fliName)?.fliDescriptionToDisplay;
        return caption != null && caption !== '' ? caption : null;
    }

    private ensureColumnCaptions(): void {
        this.workingEntries.forEach((entry) => {
            if (entry.customColumnName) {
                return;
            }
            const caption = this.resolveCaption(entry.fliName);
            if (caption != null) {
                entry.customColumnName = caption;
            }
        });
    }

    private displayNameFor(fliName: string): string {
        const caption = this.resolveCaption(fliName);
        if (caption != null) {
            return caption;
        }
        const words = fliName.replace(/[_-]+/g, ' ').trim();
        return words.charAt(0).toUpperCase() + words.slice(1);
    }


    private movementsForNewColumn(reference: IEquityMovement[] | undefined): IEquityMovement[] {
        return (reference ?? []).map((movement) => ({ name: movement.name, value: null }));
    }

    onApplyStructureChanges(): void {
        const { newFliNames, removedFliNames } = this.structureDiff;
        const removed = new Set(removedFliNames);
        const reference = this.workingEntries[0];

        const valueColumnIds = this.workingEntries
            .filter((entry) => (entry.customColType ?? 'SYSTEM') !== 'TOTAL')
            .map((entry) => entry.fliName);
        const totalsSummingEverything = new Set(
            this.workingEntries
                .filter(
                    (entry) =>
                        entry.customColType === 'TOTAL' &&
                        valueColumnIds.every((id) => (entry.sumUpTotalColIDs ?? []).includes(id)),
                )
                .map((entry) => entry.fliName),
        );

        const kept = this.workingEntries.filter((entry) => !removed.has(entry.fliName));
        const added: IEquityMovementEntry[] = newFliNames.map((fliName) => ({
            fliName,
            customColType: 'SYSTEM',
            previousMovements: this.movementsForNewColumn(reference?.previousMovements),
            currentMovements: this.movementsForNewColumn(reference?.currentMovements),
        }));

        this.workingEntries = [...added, ...kept];
        this.workingEntries.forEach((entry) => {
            if (entry.customColType !== 'TOTAL') {
                return;
            }
            const sources = (entry.sumUpTotalColIDs ?? []).filter((id) => !removed.has(id));
            entry.sumUpTotalColIDs = totalsSummingEverything.has(entry.fliName)
                ? [...sources, ...newFliNames]
                : sources;
        });

        this.structureChangesHandled = true;
        this.rebuildColumns();
        this.emitSchedule();
    }

    onDismissStructureBanner(): void {
        this.structureChangesHandled = true;
    }

    onAddColumn(type: 'USERDEFINED' | 'TOTAL'): void {
        const reference = this.workingEntries[0];
        const newEntry: IEquityMovementEntry = {
            fliName: `${type === 'TOTAL' ? 'TOTAL_' : 'USER_'}${uuidv4()}`,
            customColType: type,
            customColumnName:
                type === 'TOTAL'
                    ? this.tContent('aumico_annual_report_ems_new_total_column')
                    : this.tContent('aumico_annual_report_ems_manual_column'),
            sumUpTotalColIDs:
                type === 'TOTAL'
                    ? this.workingEntries.filter((entry) => entry.customColType !== 'TOTAL').map((entry) => entry.fliName)
                    : undefined,
            previousMovements: this.movementsForNewColumn(reference?.previousMovements),
            currentMovements: this.movementsForNewColumn(reference?.currentMovements),
        };
        this.workingEntries = [...this.workingEntries, newEntry];
        this.rebuildColumns();
        this.emitSchedule();
    }

    private assembleSchedule(): IEquityMovementSchedule {
        const aboveCtrl = this.formGroup.get('textAdditionAboveEms');
        const belowCtrl = this.formGroup.get('textAdditionBelowEms');
        return {
            equityMovementEntries: cloneDeep<IEquityMovementEntry[]>(this.workingEntries),
            textAdditionAboveEms: aboveCtrl != null ? aboveCtrl.value : null,
            textAdditionBelowEms: belowCtrl != null ? belowCtrl.value : null,
            previousOpeningTitle: this.previousOpeningTitle,
            previousClosingTitle: this.previousClosingTitle,
            currentOpeningTitle: this.currentOpeningTitle,
            currentClosingTitle: this.currentClosingTitle,
        };
    }

    private emitSchedule(): void {
        this.equityMovementScheduleValue.emit(this.assembleSchedule());
    }

    private buildExtensions(): AnyExtension[] {
        return [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];
    }

    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) {
                return;
            }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) {
                    button.classList.add('is-selected');
                }
                button.textContent = item;
                button.addEventListener('click', () => {
                    currentCommand?.({ id: item, label: item });
                });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) {
                return;
            }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) {
                        return false;
                    }
                    if (currentItems.length === 0) {
                        return false;
                    }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') {
                        return true;
                    }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }

    private ensureTiptapEditors(): void {
        const extensions = this.buildExtensions();

        if (!this.tiptapEditorAbove && this.tiptapHostAbove?.nativeElement) {
            this.tiptapEditorAbove = new Editor({
                element: this.tiptapHostAbove.nativeElement,
                extensions,
                content: this.tiptapValueAbove,
                onUpdate: ({ editor }) => this.handleTiptapUpdateAbove(editor.getHTML()),
            });
        }

        if (!this.tiptapEditorBelow && this.tiptapHostBelow?.nativeElement) {
            this.tiptapEditorBelow = new Editor({
                element: this.tiptapHostBelow.nativeElement,
                extensions,
                content: this.tiptapValueBelow,
                onUpdate: ({ editor }) => this.handleTiptapUpdateBelow(editor.getHTML()),
            });
        }
    }

    private syncTiptapFromForm(): void {
        const aboveValue = (this.formGroup?.get('textAdditionAboveEms')?.value as string | null) ?? '';
        const belowValue = (this.formGroup?.get('textAdditionBelowEms')?.value as string | null) ?? '';

        this.tiptapValueAbove = applyColgroupWidths(aboveValue);
        this.tiptapValueBelow = applyColgroupWidths(belowValue);

        if (this.tiptapEditorAbove) {
            this.tiptapEditorAbove.commands.setContent(this.tiptapValueAbove, false);
        }
        if (this.tiptapEditorBelow) {
            this.tiptapEditorBelow.commands.setContent(this.tiptapValueBelow, false);
        }
    }

    private handleTiptapUpdateAbove(html: string): void {
        this.tiptapValueAbove = html;
        const control = this.formGroup?.get('textAdditionAboveEms');
        if (control && control.value !== html) {
            control.setValue(html);
        }
    }

    private handleTiptapUpdateBelow(html: string): void {
        this.tiptapValueBelow = html;
        const control = this.formGroup?.get('textAdditionBelowEms');
        if (control && control.value !== html) {
            control.setValue(html);
        }
    }

    async onTiptapImageSelectedAbove(file: File): Promise<void> {
        await this.insertImageInEditor(this.tiptapEditorAbove, file);
    }

    async onTiptapImageSelectedBelow(file: File): Promise<void> {
        await this.insertImageInEditor(this.tiptapEditorBelow, file);
    }

    private async insertImageInEditor(editor: Editor | null, file: File): Promise<void> {
        if (!editor || !file?.type?.startsWith('image/')) {
            return;
        }

        try {
            const base64 = await convertFileToBase64(file);
            if (!base64) {
                return;
            }
            editor.commands?.focus?.();
            (editor as any)?.commands?.setImage?.({ src: base64 });
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    }
}
